<!--
Component to display an interactive slide show representing one chapter of a
presentation, plus a nav to change chapters
-->
<template>
	<div class="flex h-full flex-col bg-white select-none">
		<FuneralHomeAssistant v-if="showAssistant" />
		<div class="w-full grow overflow-auto">
			<FormKitProvider :config="formKitConfig">
				<PresentationNav v-if="showNavBar" />
				<!-- Note: The border on print is necessary to get the page to display correctly without giant gaps between sections on the memorial guide. -->
				<swiper-container
					:init="false"
					class="h-full w-full print:h-auto print:border print:border-transparent"
					ref="swiper"
					speed="500"
					:mousewheel="false"
					:keyboard="true"
					effect="slide"
				>
					<!-- This loop renders only the subset of slides near the active slide -->
					<swiper-slide
						:class="{
							'precoa-slide-active': slide._uid === controller.activeSlideId,
						}"
						:style="{ left: virtualData.offset + 'px' }"
						v-for="slide in virtualData.slides"
						:key="slide._uid"
					>
						<LazyRender
							render-on-idle
							class="h-full"
						>
							<component
								v-if="swiper?.swiper"
								@submit="controller.onSlideSubmit"
								:is="slide.component"
								:blok="slide"
								:parentSwiper="swiper?.swiper"
								@goto-next-chapter="controller.gotoNextChapter"
								@goto-chapter="controller.gotoChapter"
								:active="controller.activeSlideId == slide._uid"
								:inert="controller.activeSlideId !== slide._uid"
							></component
						></LazyRender>
					</swiper-slide>
				</swiper-container>
			</FormKitProvider>
		</div>
	</div>
</template>

<script setup lang="ts">
import LazyRender from '../common/LazyRender.vue'
import { localizedConfig } from '@/../formkit.config'
import FuneralHomeAssistant from '@/components/funeral-home-assistant/FuneralHomeAssistant.vue'
import PresentationNav from '@/components/presentation/PresentationNav.vue'
import { usePresentationLocale } from '@/i18n'
import router from '@/router'
import type { SlideAbstract } from '@/sbTypes'
import { usePresentationController } from '@/stores/presentationController'
import { type FormKitOptions } from '@formkit/core'
import { FormKitProvider } from '@formkit/vue'
import { captureException } from '@sentry/vue'
import { usePointer } from '@vueuse/core'
import { register, type SwiperContainer } from 'swiper/element/bundle'
import type { SwiperOptions } from 'swiper/types'
import { computed, ref, onMounted, watch, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

type VirtualData = {
	from: number
	to: number
	offset: number
	slides: SlideAbstract[]
}

const props = defineProps({
	mode: {
		type: String,
		default: 'SlideDeck',
	},
	initialSlideIndex: {
		type: Number,
		default: 0,
	},
	/** When modified, Swiper will re-render */
	deckVersion: {
		type: Number,
		default: undefined,
	},
})

const { pointerType } = usePointer()
const controller = usePresentationController()
const { locale } = usePresentationLocale()

const emit = defineEmits(['slidechange'])

/** Metadata for which slides should be currently rendered */
const virtualData = ref<VirtualData>({ from: 0, to: 0, offset: 0, slides: [] })

/** The swiper element, which has a reference to the Swiper API */
const swiper = ref<SwiperContainer>()

/** The WakeLock sentinel object */
const wakeLock = ref<WakeLockSentinel>()

/** True if the user is using a mouse */
const mouseMode = computed(() => pointerType.value === 'mouse')

/** Config to localize FormKit according to the presentation */
const formKitConfig = computed<FormKitOptions>(() => localizedConfig(locale))

const showNavBar = computed(() => props.mode === 'SlideDeck')
const showAssistant = computed(() => props.mode === 'FuneralHome')

/**
 * Check if there is a target slide in the query and navigate to it if found.
 * Remove the slide query param after navigation.
 */
onMounted(async () => {
	await nextTick()

	const targetSlide = route.query.slide
	if (targetSlide) {
		const foundSlide = controller.allSlides?.find(
			(slide) => slide.component === targetSlide
		)

		if (foundSlide) {
			controller.gotoSlideId(foundSlide._uid)
			router.replace({ query: { ...route.query, slide: undefined } })
		} else {
			throw new Error(`Slide "${targetSlide}" not found.`)
		}
	}
})
/**
 * Apply mouse settings to slide presentation.
 * This should be possible with dynamic bindings to
 * grab-cursor and allow-touch-move, but it wasn't working.
 */
watch(mouseMode, (newValue) => {
	if (!swiper.value || !swiper.value.swiper) {
		return
	}

	if (newValue) {
		swiper.value.swiper.unsetGrabCursor()
		swiper.value.swiper.allowTouchMove = false
	} else {
		swiper.value.swiper.setGrabCursor()
		swiper.value.swiper.allowTouchMove = true
	}
})

/** Set up Swiper and the presentation controller */
onMounted(async () => {
	// Register the Swiper element
	register()

	configureSwiper()

	controller.setInitialSlideIndex(props.initialSlideIndex)

	// Emit an event when the slide changes
	swiper.value?.addEventListener('swiperslidechange', (event: Event) => {
		const customEvent = event as CustomEvent
		if (customEvent.target !== swiper.value) {
			return
		}
		emit(
			'slidechange',
			customEvent.detail[0].activeIndex,
			customEvent.detail[0].previousIndex
		)
	})

	if (!swiper.value) {
		throw new Error('Swiper was unable to mount')
	}

	controller.swiper = swiper.value.swiper

	// Device detection
	const userAgent = navigator.userAgent
	const isMobileOrTablet =
		/iPhone|iPad|Android/.test(userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
	const isDesktop = !isMobileOrTablet

	// Check if the app is running in standalone mode
	const isStandalone =
		(window.navigator as Navigator & { standalone?: boolean }).standalone ||
		window.matchMedia('(display-mode: standalone)').matches

	const isMobileStandalone = isMobileOrTablet && isStandalone

	if (isMobileStandalone || isDesktop) {
		// Skip fullscreen request if requestFullscreen api is not supported
		if (!document.body.requestFullscreen) {
			return
		}

		try {
			// Go to fullscreen mode
			await document.body.requestFullscreen({
				navigationUI: 'hide',
			})
		} catch (e) {
			captureException(e)
			console.warn('Could not enter fullscreen', e)
		}

		// Enable WakeLock
		try {
			wakeLock.value = await navigator.wakeLock?.request('screen')
			console.info('WakeLock acquired')
		} catch (e) {
			console.warn('Could not lock screen', e)
		}
	} else {
		console.info(
			'Fullscreen not applied: App is not in standalone mode or is being viewed on an unsupported device.'
		)
	}

	swiper.value.swiper.params.keyboard = {
		enabled: false,
	}

	setTimeout(() => {
		swiper.value?.swiper.update()
	}, 250)
})

// Send our virtual slide options as attributes to the swiper element
// https://swiperjs.com/element#virtual-slides
function configureSwiper() {
	const swiperOptions: SwiperOptions = {
		initialSlide: props.initialSlideIndex,
		noSwipingSelector: '[role="radio"],.formkit-options,.swiper-no-swiping',

		virtual: {
			enabled: true,
			slides: controller.allSlides || [],
			addSlidesBefore: 0,
			addSlidesAfter: 0,

			/**
			 * Called when Swiper determines that the set of rendered slides
			 * should change. We just need to pass it to our observable ref.
			 */
			renderExternal: (data: VirtualData) => {
				virtualData.value = {
					...data,
				}
			},
		},
	}

	const swiperEl = document.querySelector('swiper-container')
	if (!swiperEl) {
		throw new Error('Swiper was unable to mount')
	}
	Object.assign(swiperEl, swiperOptions)
	swiperEl.initialize()
}

/** Clean up when leaving */
onBeforeUnmount(() => {
	// Remove chapter data from the presentation controller
	controller.reset()

	// Release WakeLock
	wakeLock.value
		?.release()
		.catch((e) => console.warn('Could not release WakeLock', e))
})

// Handle idle → active reactivation (prevents slide jump after being idle)
function handleVisibilityChange() {
	if (document.visibilityState === 'visible' && swiper.value?.swiper) {
		const s = swiper.value.swiper
		s.allowSlideNext = false
		s.allowSlidePrev = false
		setTimeout(() => {
			s.update()
			s.allowSlideNext = true
			s.allowSlidePrev = true
		}, 300)
	}
}

/** Update slides when deckVersion changes */
watch(
	() => props.deckVersion,
	() => {
		if (!swiper.value) return
		swiper.value.swiper.virtual.slides = controller.allSlides || []
		swiper.value.swiper.virtual.update(true)
	}
)

onMounted(() => {
	document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
	document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>
